import { X } from "lucide-react";

import { Button } from "../../utils/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../../utils/components/ui/dialog";
import { Input } from "../../utils/components/ui/input";

import { PosSettlementTransactionPayload } from "../../types/posSettlementTypes";

type SettlePosDialogProps = {
  open: boolean;
  formState: PosSettlementTransactionPayload;
  isSubmitting?: boolean;
  expectedAmount?: number;
  onOpenChange: (open: boolean) => void;
  onFieldChange: (
    field: keyof PosSettlementTransactionPayload,
    value: string | number,
  ) => void;
  onSubmit: () => void;
};

function SettlePosDialog({
  open,
  formState,
  isSubmitting = false,
  expectedAmount,
  onOpenChange,
  onFieldChange,
  onSubmit,
}: SettlePosDialogProps) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        showCloseButton={false}
        className="max-w-[920px] rounded-[28px] border border-[#E8EDF3] bg-white p-0 shadow-[0_24px_64px_-28px_rgba(15,23,42,0.45)]"
      >
        <div className="p-6 sm:p-8">
          <DialogHeader className="flex-row items-start justify-between gap-4 text-left">
            <div>
              <DialogTitle className="text-3xl font-semibold tracking-tight text-[#1A5EF9]">
                Settle POS
              </DialogTitle>
              <DialogDescription className="mt-2 text-sm text-slate-500">
                Capture the settlement transaction for the selected POS session.
              </DialogDescription>
            </div>
            <button
              type="button"
              onClick={() => onOpenChange(false)}
              className="rounded-full p-2 text-slate-400 transition hover:bg-slate-100 hover:text-slate-600"
            >
              <span className="sr-only">Close</span>
              <X className="h-4 w-4" />
            </button>
          </DialogHeader>

          <div className="mt-8 grid gap-6">
            <div className="grid gap-6 md:grid-cols-2">
              <div className="space-y-2">
                <label className="text-sm font-medium text-slate-600">
                  Session ID
                </label>
                <Input
                  value={formState.sessionId}
                  disabled
                  className="h-12 rounded-xl border-[#E6ECF5] bg-[#F8FAFC]"
                />
              </div>
            </div>

            <div className="grid gap-6 md:grid-cols-3">
              <div className="space-y-2 md:col-span-2">
                <label className="text-sm font-medium text-slate-600">
                  Amount
                </label>
                <Input
                  type="number"
                  min="0"
                  max={expectedAmount}
                  step="any"
                  value={formState.amount}
                  onChange={(event) =>
                    onFieldChange("amount", Number(event.target.value))
                  }
                  className="h-12 rounded-xl border-[#E6ECF5] bg-[#F8FAFC]"
                />
              </div>
            </div>
          </div>

          <DialogFooter className="mt-8 border-t border-[#E8EDF3] pt-6 sm:justify-end">
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
              className="h-11 min-w-[112px] rounded-xl border-[#D8E1EC] px-6 text-slate-600"
            >
              Cancel
            </Button>
            <Button
              type="button"
              onClick={onSubmit}
              disabled={isSubmitting}
              className="h-11 min-w-[112px] rounded-xl bg-[#1A5EF9] px-6 text-white hover:bg-[#1A5EF9]/80"
            >
              {isSubmitting ? "Settling..." : "Settle POS"}
            </Button>
          </DialogFooter>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export default SettlePosDialog;
